import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { PlayArrowRounded } from '@mui/icons-material';
import AuthFlowLayout from './AuthFlowLayout';
import PrimaryActionButton from './PrimaryActionButton';
import { ProfilePicture } from './ProfilePicture';
import './OnboardingHighlights.css';

const durationLabel = duration => {
    const seconds = Math.round(Number(duration));
    if (!Number.isFinite(seconds) || seconds <= 0) return null;
    if (seconds < 60) return `${seconds} sec`;
    const remainder = seconds % 60;
    return `${Math.floor(seconds / 60)} min${remainder ? ` ${remainder} sec` : ''}`;
};

const categoryLabel = preview => {
    const category = preview?.starterTopic;
    return typeof category === 'string'
        ? category.replace(/[-_]/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase())
        : '';
};

const HighlightAvatar = ({ preview, size = '48px' }) => <span
    className='onboardingHighlightAvatar' aria-hidden='true'>
    <ProfilePicture speakerId={preview.person?.id} imageSrc={preview.person?.imageURL}
        size={size} borderSize={1} />
</span>;

export default function OnboardingHighlightsReady({
    sample,
    choices = [],
    playLabel = 'Hear your first highlight',
    onPlay,
    onSelect,
    onChangePicks,
    error = null,
    pending = false,
}) {
    const headingRef = useRef(null);
    const cardsRef = useRef(null);
    const pagerRef = useRef(null);
    const settleTimer = useRef(null);
    const settleScrollRef = useRef(null);
    const previousCardIds = useRef(null);
    const touchingCards = useRef(false);
    const nativeTouchActive = useRef(false);
    const heightMeasurement = useRef({ context: null, height: 0 });
    const [browsing, setBrowsing] = useState(false);
    const [cardHeight, setCardHeight] = useState(0);
    const highlightTitleId = useId();
    const highlightCardId = useId();
    const errorId = useId();
    const selectedIndex = choices.findIndex(preview => preview.id === sample?.id);
    const showNavigation = choices.length > 1 && selectedIndex >= 0 && Boolean(onSelect);
    const displayedHighlights = showNavigation ? choices : sample ? [sample] : [];
    const cardIds = JSON.stringify(displayedHighlights.map(preview => preview.id));

    useEffect(() => {
        headingRef.current?.focus({ preventScroll: true });
        return () => clearTimeout(settleTimer.current);
    }, []);

    useLayoutEffect(() => {
        const cards = cardsRef.current;
        if (!cards) return undefined;
        const measure = () => {
            const typography = ['.onboardingHighlightHeroTitle', '.onboardingHighlightHeroName', '.onboardingHighlightHeroCategory']
                .map(selector => {
                    const node = cards.querySelector(selector);
                    const style = window.getComputedStyle(node || cards);
                    return [style.fontFamily, style.fontSize, style.fontWeight, style.lineHeight, style.letterSpacing];
                });
            const context = JSON.stringify([cards.clientWidth, typography]);
            // Read the natural grid height without the previous minimum hiding a shrink.
            const previousMinimum = cards.style.minHeight;
            cards.style.minHeight = '';
            const naturalHeight = cards.getBoundingClientRect().height;
            cards.style.minHeight = previousMinimum;
            const previous = heightMeasurement.current;
            const height = previous.context === context ? Math.max(previous.height, naturalHeight) : naturalHeight;
            heightMeasurement.current = { context, height };
            setCardHeight(height);
        };
        measure();
        const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
        observer?.observe(cards);
        // Text still resizes when the container is held at its cached minimum.
        cards.querySelectorAll('.onboardingHighlightHero > *, .onboardingHighlightHeroIdentity > *')
            .forEach(node => observer?.observe(node));
        window.addEventListener('resize', measure);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [cardIds]);

    useLayoutEffect(() => {
        clearTimeout(settleTimer.current);
        const cards = cardsRef.current;
        if (!cards) return;
        const left = Math.max(0, selectedIndex) * cards.clientWidth;
        const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        const membershipChanged = previousCardIds.current !== cardIds;
        const animate = !membershipChanged && !reducedMotion && Math.abs(cards.scrollLeft - left) > 1;
        // Even at the target offset, cancel an earlier animation aimed elsewhere.
        cards.scrollTo({ left, behavior: animate ? 'smooth' : 'auto' });
        setBrowsing(animate);
        previousCardIds.current = cardIds;
    }, [selectedIndex, cardIds]);

    const selectIndex = (index, focusNavigation = false) => {
        if (!showNavigation || pending || index < 0 || index >= choices.length || index === selectedIndex) return;
        if (focusNavigation) pagerRef.current?.focus({ preventScroll: true });
        onSelect(choices[index]);
    };

    const navigateByKey = event => {
        const nextIndex = {
            ArrowLeft: selectedIndex - 1,
            ArrowRight: selectedIndex + 1,
            ArrowDown: selectedIndex - 1,
            ArrowUp: selectedIndex + 1,
            Home: 0,
            End: choices.length - 1,
        }[event.key];
        if (nextIndex === undefined || pending) return;
        event.preventDefault();
        selectIndex(nextIndex, true);
    };
    const selectNearestDot = event => {
        if (pending) return;
        const bounds = event.currentTarget.getBoundingClientRect();
        const index = Math.floor((event.clientX - bounds.left) / 24);
        selectIndex(Math.max(0, Math.min(choices.length - 1, index)), true);
    };
    settleScrollRef.current = () => {
        settleTimer.current = null;
        const cards = cardsRef.current;
        if (touchingCards.current) return;
        setBrowsing(false);
        if (!cards?.clientWidth || pending) return;
        selectIndex(Math.round(cards.scrollLeft / cards.clientWidth));
    };
    const handleScroll = () => {
        setBrowsing(true);
        clearTimeout(settleTimer.current);
        // Native scroll snapping owns the drag; select once the card has settled.
        settleTimer.current = setTimeout(() => settleScrollRef.current(), 150);
    };
    const releaseCards = () => {
        touchingCards.current = false;
        if (browsing) handleScroll();
    };
    const releasePointer = () => {
        // Native panning cancels pointer events while the touch stream continues.
        if (!nativeTouchActive.current) releaseCards();
    };
    const releaseTouch = event => {
        if (event.touches.length > 0) return;
        nativeTouchActive.current = false;
        releaseCards();
    };

    if (!sample) return null;
    return <AuthFlowLayout className='onboardingHighlights onboardingHighlightsReady'
        title='Your highlights are ready.' titleRef={headingRef} logoInteractive={false}
        onBack={onChangePicks} backLabel='Change your picks'>
        <p className='authFlowSubtitle onboardingHighlightsSubtitle'>
            Find your next podcast with a short listen.
        </p>

        <div ref={cardsRef} className='onboardingHighlightCards' onScroll={handleScroll}
            style={cardHeight ? { minHeight: cardHeight } : undefined}
            onPointerDown={event => {
                if (!pending && event.isPrimary !== false && ['touch', 'pen'].includes(event.pointerType)) {
                    touchingCards.current = true;
                }
            }}
            onPointerUp={releasePointer} onPointerCancel={releasePointer}
            onTouchStart={() => {
                if (!pending) {
                    nativeTouchActive.current = true;
                    touchingCards.current = true;
                }
            }}
            onTouchEnd={releaseTouch} onTouchCancel={releaseTouch}
            role='region' aria-label='Your selected highlights' aria-roledescription='carousel'
            data-pending={pending ? 'true' : undefined}>
            {displayedHighlights.map(preview => {
                const selected = preview.id === sample.id;
                const category = categoryLabel(preview);
                const duration = durationLabel(preview.totalTimeSeconds);
                return <article key={preview.id} className='onboardingHighlightHero'
                    id={selected ? highlightCardId : undefined}
                    aria-labelledby={selected ? highlightTitleId : undefined}
                    aria-hidden={selected ? undefined : 'true'} inert={selected ? undefined : ''}
                    aria-roledescription='slide'>
                    <div className='onboardingHighlightHeroSpeaker'>
                        <HighlightAvatar preview={preview} size='52px' />
                        <div className='onboardingHighlightHeroIdentity'>
                            <div className='onboardingHighlightHeroName'>{preview.person?.name || 'Your selected speaker'}</div>
                            <div className='onboardingHighlightHeroCategory'>
                                {category || 'From your picks'}
                            </div>
                        </div>
                        {duration && <span className='onboardingHighlightDuration'>{duration}</span>}
                    </div>
                    <h2 id={selected ? highlightTitleId : undefined}
                        className='onboardingHighlightHeroTitle'>{preview.title || 'Your first highlight'}</h2>
                    {preview.source?.name && <div className='onboardingHighlightPodcast'>
                        From {preview.source.name}
                    </div>}
                </article>;
            })}
        </div>

        {showNavigation && <nav className='onboardingHighlightNavigation'
            aria-label='Choose a highlight'>
            <button type='button' className='visuallyHidden' aria-label='Previous highlight' tabIndex={-1}
                aria-controls={highlightCardId} disabled={pending || selectedIndex === 0}
                onClick={() => selectIndex(selectedIndex - 1, true)}>
                Previous highlight
            </button>
            <div ref={pagerRef} className='onboardingHighlightDots' role='slider'
                tabIndex={pending ? -1 : 0} aria-label='Choose a highlight'
                aria-valuemin={1} aria-valuemax={choices.length} aria-valuenow={selectedIndex + 1}
                aria-valuetext={`Highlight ${selectedIndex + 1} of ${choices.length}`}
                aria-disabled={pending} aria-controls={highlightCardId}
                onClick={selectNearestDot} onKeyDown={navigateByKey}>
                {choices.map((preview, index) => <span key={preview.id} className='onboardingHighlightDot'
                    aria-hidden='true' data-selected={index === selectedIndex ? 'true' : undefined}>
                    <span />
                </span>)}
            </div>
            <button type='button' className='visuallyHidden' aria-label='Next highlight' tabIndex={-1}
                aria-controls={highlightCardId} disabled={pending || selectedIndex === choices.length - 1}
                onClick={() => selectIndex(selectedIndex + 1, true)}>
                Next highlight
            </button>
            <span className='visuallyHidden' role='status' aria-live='polite'>
                Highlight {selectedIndex + 1} of {choices.length}
            </span>
        </nav>}

        <div className='onboardingHighlightPlayActions'>
            {error && <p id={errorId} role='alert' className='onboardingHighlightError'>
                {typeof error === 'string' ? error : 'Couldn’t start this highlight. Please try again.'}
            </p>}
            <PrimaryActionButton className='onboardingHighlightPlayButton' onClick={onPlay}
                disabled={pending || browsing} aria-busy={pending} aria-describedby={error ? errorId : undefined}>
                {pending ? <span className='onboardingHighlightSpinner' aria-hidden='true' />
                    : <PlayArrowRounded aria-hidden='true' />}
                {pending ? 'Starting…' : error ? 'Try again' : playLabel}
            </PrimaryActionButton>
        </div>
    </AuthFlowLayout>;
}
